import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";
import { User } from "@supabase/supabase-js";
import { LogOut, ShieldCheck } from "lucide-react";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useProfile } from "@/hooks/use-profile";
import { useRolePerspective } from "@/hooks/use-role-perspective";

export default function AuthButton() {
  const { user, profile, loading } = useProfile();
  const { effectiveRole } = useRolePerspective(profile?.id, profile?.role);
  const [, setLocation] = useLocation();
  const { toast } = useToast();

  const handleLogin = () => {
    setLocation('/auth');
  };

  const handleLogout = async () => {
    console.log("Logout button clicked");
    try {
      console.log("Calling supabase.auth.signOut()...");
      
      // Supabase signOut can sometimes hang indefinitely waiting for a network response. 
      // We race it against a 2-second timeout so it never freezes the UI.
      await Promise.race([
        supabase.auth.signOut(),
        new Promise(resolve => setTimeout(resolve, 2000))
      ]);
      
      console.log("Supabase signOut finished (or timed out)");
      
      // Fallback: manually rip out the Supabase auth token from LocalStorage to guarantee logout
      for (const key in localStorage) {
         if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
            localStorage.removeItem(key);
         }
      }
      for (const key in sessionStorage) {
         if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
            sessionStorage.removeItem(key);
         }
      }
      
      // Remove keep logged in preference so the next user is asked again
      localStorage.removeItem('keep_logged_in');
      localStorage.removeItem('keep_logged_in_decided');
      sessionStorage.removeItem('keep_logged_in');
      sessionStorage.removeItem('keep_logged_in_decided');

      toast({
        title: "Signed out",
        description: "You have been successfully signed out."
      });
      
      // Force a full page reload so all cached React states and hooks are completely cleared
      console.log("Redirecting to /");
      window.location.href = '/';
    } catch (err) {
      console.error("Logout caught error:", err);
    }
  };

  if (loading) {
    return <Button variant="ghost" disabled size="sm">Loading...</Button>;
  }

  if (user && profile) {
    const isAdmin = effectiveRole === 'admin';       
    return (
      <div className="flex items-center gap-2">
        <div
          className={`overflow-hidden transition-[max-width,opacity,transform] duration-300 ease-out ${isAdmin ? "max-w-[180px] opacity-100 translate-x-0" : "max-w-0 opacity-0 -translate-x-2 pointer-events-none"}`}
          aria-hidden={!isAdmin}
        >
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLocation('/admin/dashboard')}
            title="Admin Dashboard"
            className="flex whitespace-nowrap"
            disabled={!isAdmin}
            tabIndex={isAdmin ? 0 : -1}
          >
            <ShieldCheck className="h-4 w-4 mr-2" />
            Admin Dashboard
          </Button>
        </div>
        <span className="text-sm font-medium">
          Hi, {user.user_metadata?.full_name?.split(' ')[0] || user.email?.split('@')[0] || 'User'}
        </span>
        <Button variant="ghost" size="icon" onClick={handleLogout} title="Sign Out">
          <LogOut className="h-5 w-5" />
        </Button>
      </div>
    );
  }

  return (
    <Button onClick={handleLogin} variant="default" size="sm" className="gap-2">
      <span className="inline">Login / Sign Up</span>
    </Button>
  );
}
